import { formatNumber } from '@utils/format';
import {
  // EpsonPrinter,
  // safePrintLine,
  separatorEqual,
  separatorLine,
  formatColumns,
  formatColumnsRight,
  formatUnderline,
  signatureLine,
  separatorAsterisk,
  formatTwoColumns,
  centerText,
  formatColumnsWithAlignments,
} from '@hooks/useEpsonPrinter';
import {
  Header,
  InvoiceJsonData,
  OrderSlipJsonData,
  SalesBreakdownWrapper,
  VoidInvoiceJsonData,
  VoidOrderSlipJsonData,
  XReadingJsonData,
  ZReadingJsonData,
} from '@modules/point-of-sale/interface/orders/pos-orders.interface';
import moments from 'moment';

const getTime = (dateTime) => {
  return moments(dateTime, 'MMMM D, YYYY h:mm A').format('h:mm A');
};

const getDate = (dateTime) => {
  if (!dateTime) return '';
  return moments(dateTime, 'MMMM D, YYYY h:mm A').format('MMMM D, YYYY');
};
export function generateOrderSlipTxt(
  data: OrderSlipJsonData,
  separatorNo: number,
) {
  const {
    orderSlipNo = '',
    source = '',
    tableNo = '',
    guestName = '',
    orderType = '',
    items = [],
    printedBy = '-',
    printedDateTime = '',
    transNo = '',
  } = data;

  let txt = '';
  const add = (line: string = '') => (txt += line + '\n');

  /* ================================================
     HEADER
  ================================================= */
  add(`Order Slip No: ${orderSlipNo}`);
  add(`Source: ${source}`);
  add(`Table Number: ${tableNo}`);
  add(`Guest Name: ${guestName}`);
  add(separatorLine(separatorNo));

  /* ================================================
     ORDER TYPE / REPRINT
  ================================================= */
  const orderLabel = `${orderType}`;
  add(centerText(orderLabel, separatorNo));
  add(separatorLine(separatorNo));

  /* ================================================
     SUBHEADER
  ================================================= */
  add('Please serve the following');
  add(separatorEqual(separatorNo));

  /* ================================================
     ITEMS
  ================================================= */
  items?.forEach((item: any) => {
    add(`${item?.quantity} ${item?.description}`);

    if (item?.remarks && item?.remarks.trim() !== '') {
      add(`(${item?.remarks})`);
    }

    add(''); // blank space for readability
  });

  add(separatorEqual(separatorNo));

  /* ================================================
     FOOTER
  ================================================= */
  add(`Printed By: ${printedBy}`);
  add(`Print Date/Time: ${printedDateTime}`);

  add(centerText(`TN: ${transNo}`, separatorNo));
  add(centerText('THIS DOCUMENT IS NOT', separatorNo));
  add(centerText('VALID FOR CLAIM OF INPUT TAX', separatorNo));

  return txt;
}

export function generateVoidOrderSlipTxt(
  data: VoidOrderSlipJsonData,
  separatorNo: number,
) {
  const {
    orderSlipNo = '',
    source = '',
    tableNo = '',
    guestName = '',
    items = [],
    voidRemarks = '',
    voidedBy,
    voidedDateTime,
    approvedBy,
    printedDateTime = '',
    transNo = '',
  } = data;

  let txt = '';
  const add = (line: string = '') => (txt += line + '\n');

  /* ================================================
     HEADER
  ================================================= */
  add(separatorLine(separatorNo));
  add(centerText('NOTICE OF VOIDED ITEM(S)', separatorNo));
  add(centerText(`By: ${voidedBy || '-'}`, separatorNo));
  add(centerText(voidedDateTime, separatorNo));
  add(separatorLine(separatorNo));

  add(`Order Slip No: ${orderSlipNo}`);
  add(`Source: ${source}`);
  add(`Table Number: ${tableNo}`);
  add(`Guest Name: ${guestName}`);
  add('Voided Item(s)');
  add(separatorEqual(separatorNo));
  /* ================================================
     ITEMS
  ================================================= */
  items?.forEach((item: any) => {
    add(`${item?.quantity} ${item?.description}`);

    if (item?.remarks && item?.remarks.trim() !== '') {
      add(`(${item?.remarks})`);
    }

    add(''); // blank space for readability
  });

  add(separatorEqual(separatorNo));
  if (voidRemarks && voidRemarks.trim() !== '') {
    add(`Reason: ${voidRemarks}`);
  }
  add(`Approved By: ${approvedBy}`);
  add(`Print Date/Time: ${printedDateTime}`);

  add(centerText(`TN: ${transNo}`, separatorNo));
  add(centerText('THIS DOCUMENT IS NOT', separatorNo));
  add(centerText('VALID FOR CLAIM OF INPUT TAX', separatorNo));

  return txt;
}

export function generateInvoiceTxt(
  data: InvoiceJsonData,
  header: Header,
  separatorNo: number,
) {
  const {
    breakdown,
    scPercentage,
    payments = [],
    invoiceNo = '',
    guestName = '',
    pax = '',
    tableNo = '',
    vatType = '',
    items = [],
    groupShare,
    taxpayer,
    totalAmount = 0,
    totalDeductions = 0,
    totalPayables = 0,
    regDiscount = 0,
    change = 0,
    orderType = '',
    date = '',
    time = '',
    transNo = '',
    cashier = '',
  } = data;

  /* Header info */
  const {
    propertyName = '',
    owner = '',
    street = '',
    barangay = '',
    city = '',
    province = '',
    TIN = '',
    machineNo = '',
    MIN = '',
  } = header || {};

  const {
    vatable = 0,
    vatAmount = 0,
    vatExempt = 0,
    zeroRated = 0,
    serviceCharge = 0,
    localTax = 0,
    serviceChargeDeduction = 0,
    localTaxDeduction = 0,
    vatDeduction = 0,
    discNetOfVat = 0,
  } = breakdown || {};

  /* Group share */
  const {
    regPax = 0,
    scPax = 0,
    spPax = 0,
    athPax = 0,
    pwdPax = 0,
    regShare = 0,
    scShare = 0,
    spShare = 0,
    athShare = 0,
    pwdShare = 0,
    scDetails,
    pwdDetails,
    athleteDetails,
    soloParentDetails,
  } = groupShare || {};
  const { taxpayerName, guestTIN, guestAddress } = taxpayer || {};
  let txt = '';

  const add = (line: string = '') => (txt += line + '\n');
  /* ======================================================
     HEADER
  ====================================================== */
  add(centerText(propertyName, separatorNo));
  add(centerText(owner, separatorNo));
  add(centerText(street, separatorNo));
  add(centerText(`${barangay} ${city} ${province}`, separatorNo));
  add(centerText(`${TIN}`, separatorNo));
  add(centerText(`MIN: ${MIN} / S/N: ${machineNo}`, separatorNo));

  /* Invoice header */
  add(centerText('INVOICE', separatorNo));
  add(centerText(invoiceNo, separatorNo));

  if (vatType === 'E') add(centerText('VAT Exempt Sales', separatorNo));
  if (vatType === 'Z') add(centerText('Zero-Rated Sales', separatorNo));

  /* ======================================================
     INFO LINES
  ====================================================== */
  add(
    formatColumns([`Guest: ${guestName}`, `Pax: ${pax}`], [1, 2], separatorNo),
  );

  add(formatColumns([`Table No: ${tableNo}`, orderType], [1, 2], separatorNo));

  add(formatColumns([`Date: ${date}`, `Time: ${time}`], [1, 2], separatorNo));

  add(`Cashier: ${cashier}`);
  add('');

  /* ======================================================
     ITEM HEADER
  ====================================================== */
  add(
    formatColumnsWithAlignments(
      ['Qty', 'Item', 'U/P', 'Amount'],
      [1, 4, 1, 2],
      separatorNo,
      ['left', 'left', 'right', 'right'],
    ),
  );
  add(separatorEqual(separatorNo));

  /* Items */
  items?.forEach((item) => {
    const qty = item?.quantity ?? 0;
    const description = item?.description ?? '';
    const amount = formatNumber(item?.amount ?? 0, 2, 2);
    const price = formatNumber(item?.price ?? 0, 2, 2);
    const remarks = item?.discountRemarks?.trim() || '';

    add(
      formatColumnsWithAlignments(
        [qty, description, price, amount],
        [1, 4, 1, 2],
        separatorNo,
        ['left', 'left', 'right', 'right'],
      ),
    );

    if (remarks !== '') {
      add(formatColumns(['', `(${remarks})`, ''], [1, 3, 2], separatorNo));
    }
  });

  add(separatorEqual(separatorNo));

  /* ======================================================
     TOTALS
  ====================================================== */
  add(
    formatColumnsRight(
      ['Total Amount:', formatNumber(totalAmount, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );

  if (totalDeductions > 0) {
    add(
      formatColumnsRight(
        ['Total Deductions:', formatNumber(totalDeductions, 2, 2)],
        separatorNo,
        [1, 1],
      ),
    );
  }

  add(
    formatColumnsRight(
      ['Total Payables:', formatNumber(totalPayables, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );
  payments?.forEach((payment) => {
    if (payment.total_amount > 0) {
      add(
        formatColumnsRight(
          [`${payment?.name}:`, formatNumber(payment?.total_amount, 2, 2)],
          separatorNo,
          [1, 1],
        ),
      );
    }
  });

  add(
    formatColumnsRight(
      ['Change:', formatNumber(change, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );
  payments?.forEach((payment) => {
    if (payment?.details && payment?.details?.length > 0) {
      add(`${payment?.name}`);
      payment?.details?.forEach((detail) => {
        const remarks = detail?.remarks || '';
        add(`   ${remarks}`);
      });
    }
  });
  /* ======================================================
    GROUP SHARE & SIGNATURES
 ====================================================== */
  const hasAnyValue =
    scPax > 0 ||
    spPax > 0 ||
    athPax > 0 ||
    pwdPax > 0 ||
    scShare > 0 ||
    spShare > 0 ||
    athShare > 0 ||
    pwdShare > 0;

  const hasDetails =
    (scDetails && scDetails?.length > 0) ||
    (pwdDetails && pwdDetails?.length > 0) ||
    (athleteDetails && athleteDetails?.length > 0) ||
    (soloParentDetails && soloParentDetails?.length > 0);

  if (groupShare && typeof groupShare === 'object' && hasAnyValue) {
    add(separatorEqual(separatorNo));

    let totalPax = 0;

    // -----------------------
    // REGULAR
    // -----------------------
    if (regShare > 0) {
      add(
        formatColumnsRight(
          ['REG Share:', formatNumber(regShare, 2, 2)],
          separatorNo,
          [1, 1],
        ),
      );
    }
    if (regPax > 0) {
      add(formatColumnsRight(['Total REG Pax:', regPax], separatorNo, [1, 1]));
    }

    // -----------------------
    // SENIOR CITIZEN
    // -----------------------
    if (scPax > 0 && scShare > 0) {
      add(
        formatColumnsRight(
          ['SC Share:', formatNumber(scShare, 2, 2)],
          separatorNo,
          [1, 1],
        ),
      );
      add(formatColumnsRight(['Total SC Pax:', scPax], separatorNo, [1, 1]));
      totalPax += scPax;
    }

    // -----------------------
    // SOLO PARENT
    // -----------------------
    if (spPax > 0 && spShare > 0) {
      add(
        formatColumnsRight(
          ['SP Share:', formatNumber(spShare, 2, 2)],
          separatorNo,
          [1, 1],
        ),
      );
      add(formatColumnsRight(['Total SP Pax:', spPax], separatorNo, [1, 1]));
      totalPax += spPax;
    }

    // -----------------------
    // ATHLETE / NAAC
    // -----------------------
    if (athPax > 0 && athShare > 0) {
      add(
        formatColumnsRight(
          ['NAAC Share:', formatNumber(athShare, 2, 2)],
          separatorNo,
          [1, 1],
        ),
      );
      add(formatColumnsRight(['Total NAAC Pax:', athPax], separatorNo, [1, 1]));
      totalPax += athPax;
    }

    // -----------------------
    // PWD
    // -----------------------
    if (pwdPax > 0 && pwdShare > 0) {
      add(
        formatColumnsRight(
          ['PWD Share:', formatNumber(pwdShare, 2, 2)],
          separatorNo,
          [1, 1],
        ),
      );
      add(formatColumnsRight(['Total PWD Pax:', pwdPax], separatorNo, [1, 1]));
      totalPax += pwdPax;
    }

    // -----------------------
    // CUSTOMER DETAILS
    // -----------------------
    if (hasDetails) {
      add('CUSTOMER DETAILS');

      scDetails?.forEach(({ idNo, name, tin }) => {
        add(formatTwoColumns('SC Name:', name, separatorNo));
        if (tin) add(formatTwoColumns('SC TIN No.:', tin, separatorNo));
        add(formatTwoColumns('SC ID No.:', idNo, separatorNo));
      });

      pwdDetails?.forEach(({ idNo, name, tin }) => {
        add(formatTwoColumns('PWD Name:', name, separatorNo));
        if (tin) add(formatTwoColumns('PWD TIN No.:', tin, separatorNo));
        add(formatTwoColumns('PWD ID No.:', idNo, separatorNo));
      });

      athleteDetails?.forEach(({ idNo, name }) => {
        add(formatTwoColumns('NAAC Name:', name, separatorNo));
        add(formatTwoColumns('NAAC ID No.:', idNo, separatorNo));
      });

      soloParentDetails?.forEach(({ idNo, name }) => {
        add(formatTwoColumns('SP Name:', name, separatorNo));
        add(formatTwoColumns('SP ID:', idNo, separatorNo));
      });
    }

    // -----------------------
    // SIGNATURES
    // -----------------------
    add(centerText('Signature(s)', separatorNo));
    for (let i = 0; i < totalPax; i++) {
      add(signatureLine(separatorNo));
    }
  }

  add(separatorEqual(separatorNo));

  /* ======================================================
     VAT BREAKDOWN
  ====================================================== */
  add(
    formatColumnsRight(
      ['VATable Sales:', formatNumber(vatable, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );
  add(
    formatColumnsRight(
      ['VAT Amount:', formatNumber(vatAmount, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );
  add(
    formatColumnsRight(
      ['VAT-Exempt Sales:', formatNumber(vatExempt, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );
  add(
    formatColumnsRight(
      ['Zero-Rated Sales:', formatNumber(zeroRated, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );

  if (serviceCharge > 0 || serviceChargeDeduction > 0)
    add(
      formatColumnsRight(
        [
          `Service Charge (${scPercentage}%):`,
          formatNumber(serviceCharge, 2, 2),
        ],
        separatorNo,
        [1, 1],
      ),
    );

  if (localTax > 0 || localTaxDeduction > 0)
    add(
      formatColumnsRight(
        ['Local Tax:', formatNumber(localTax, 2, 2)],
        separatorNo,
        [1, 1],
      ),
    );

  if (serviceChargeDeduction > 0)
    add(
      formatColumnsRight(
        [
          'Service Charge Deductions:',
          formatNumber(serviceChargeDeduction, 2, 2),
        ],
        separatorNo,
        [1, 1],
      ),
    );

  if (localTaxDeduction > 0)
    add(
      formatColumnsRight(
        ['Local Tax Deductions:', formatNumber(localTaxDeduction, 2, 2)],
        separatorNo,
        [1, 1],
      ),
    );

  if (vatDeduction > 0)
    add(
      formatColumnsRight(
        ['VAT Deductions:', formatNumber(vatDeduction, 2, 2)],
        separatorNo,
        [1, 1],
      ),
    );

  if (discNetOfVat > 0)
    add(
      formatColumnsRight(
        ['Disc Net of VAT:', formatNumber(discNetOfVat, 2, 2)],
        separatorNo,
        [1, 1],
      ),
    );

  add(separatorEqual(separatorNo));

  /* ======================================================
     SIGNATURE SECTION
  ====================================================== */
  add(
    `${taxpayerName ? `Name: ${taxpayerName}` : formatUnderline('Name:', separatorNo)}`,
  );
  add(
    `${guestTIN ? `TIN: ${guestTIN}` : formatUnderline('TIN:', separatorNo)}`,
  );
  add(
    `${guestAddress ? `Address: ${guestAddress}` : formatUnderline('Address:', separatorNo)}`,
  );
  add('');

  if (regDiscount > 0) {
    add(centerText(signatureLine(separatorNo), separatorNo));
    add(centerText('Discount Authorized By', separatorNo));
    add('');
    add(centerText(signatureLine(separatorNo), separatorNo));
    add(centerText('Customer Signature', separatorNo));
    add('');
  }

  /* ======================================================
     FOOTER
  ====================================================== */
  add(centerText('POS Provider:', separatorNo));
  add(centerText('SERVO I.T. SOLUTIONS OPC', separatorNo));
  add(centerText('Pio Del Pilar, Makati City', separatorNo));
  add(centerText('PTU: FP082021-000-0000000-00000', separatorNo));
  add(centerText('THIS SERVES AS AN INVOICE', separatorNo));
  add(centerText(`TN: ${transNo}`, separatorNo));
  add(centerText('Thank you. Please come again', separatorNo));

  return txt;
}

export function generateVoidInvoiceTxt(
  data: VoidInvoiceJsonData,
  header: Header,
  separatorNo: number,
) {
  const {
    breakdown,
    scPercentage,
    payments = [],
    siNo = '',
    invoiceNo = '',
    tableNo = '',
    vatType = '',
    items = [],
    groupShare,
    taxpayer,
    totalAmount = 0,
    totalDeductions = 0,
    totalPayables = 0,
    regDiscount = 0,
    change = 0,
    orderType = '',
    date = '',
    time = '',
    voidedDateTime,
    voidedBy,
    transNo = '',
    cashier = '',
    guestName = '',
    pax = '',
  } = data;

  /* Header info */
  const {
    propertyName = '',
    owner = '',
    street = '',
    barangay = '',
    city = '',
    province = '',
    TIN = '',
    machineNo = '',
    MIN = '',
  } = header || {};

  const {
    vatable = 0,
    vatAmount = 0,
    vatExempt = 0,
    zeroRated = 0,
    serviceCharge = 0,
    localTax = 0,
    serviceChargeDeduction = 0,
    localTaxDeduction = 0,
    vatDeduction = 0,
    discNetOfVat = 0,
  } = breakdown || {};

  /* Group share */
  const {
    regPax = 0,
    scPax = 0,
    spPax = 0,
    athPax = 0,
    pwdPax = 0,
    regShare = 0,
    scShare = 0,
    spShare = 0,
    athShare = 0,
    pwdShare = 0,
    scDetails,
    pwdDetails,
    athleteDetails,
    soloParentDetails,
  } = groupShare || {};
  const { taxpayerName, guestTIN, guestAddress } = taxpayer || {};
  let txt = '';

  const add = (line: string = '') => (txt += line + '\n');
  /* ======================================================
     HEADER
  ====================================================== */
  add(centerText(propertyName, separatorNo));
  add(centerText(owner, separatorNo));
  add(centerText(street, separatorNo));
  add(centerText(`${barangay} ${city} ${province}`, separatorNo));
  add(centerText(`${TIN}`, separatorNo));
  add(centerText(`MIN: ${MIN} / S/N: ${machineNo}`, separatorNo));

  /* Invoice header */
  add(centerText('VOID INVOICE', separatorNo));
  add(centerText(`REF: ${siNo}`, separatorNo));
  add(centerText(`By: ${voidedBy || '-'}`, separatorNo));
  add(centerText(`${voidedDateTime}`, separatorNo));
  add(centerText(invoiceNo, separatorNo));

  if (vatType === 'E') add(centerText('VAT Exempt Sales', separatorNo));
  if (vatType === 'Z') add(centerText('Zero-Rated Sales', separatorNo));

  /* ======================================================
     INFO LINES
  ====================================================== */
  add(
    formatColumns([`Guest: ${guestName}`, `Pax: ${pax}`], [1, 2], separatorNo),
  );
  add(formatColumns([`Table No: ${tableNo}`, orderType], [1, 2], separatorNo));
  add(formatColumns([`Date: ${date}`, `Time: ${time}`], [1, 2], separatorNo));
  add(`Cashier: ${cashier}`);
  /* ======================================================
     ITEM HEADER
  ====================================================== */
  add(
    formatColumnsWithAlignments(
      ['Qty', 'Item', 'U/P', 'Amount'],
      [1, 4, 1, 2],
      separatorNo,
      ['left', 'left', 'right', 'right'],
    ),
  );
  add(separatorEqual(separatorNo));

  /* Items */
  items?.forEach((item) => {
    const qty = item?.quantity ?? 0;
    const description = item?.description ?? '';
    const amount = formatNumber(item?.amount ?? 0, 2, 2);
    const price = formatNumber(item?.price ?? 0, 2, 2);
    const remarks = item?.discountRemarks?.trim() || '';

    add(
      formatColumnsWithAlignments(
        [qty, description, price, amount],
        [1, 4, 1, 2],
        separatorNo,
        ['left', 'left', 'right', 'right'],
      ),
    );

    if (remarks !== '') {
      add(formatColumns(['', `(${remarks})`, ''], [1, 3, 2], separatorNo));
    }
  });

  add(separatorEqual(separatorNo));

  /* ======================================================
     TOTALS
  ====================================================== */
  add(
    formatColumnsRight(
      ['Total Amount:', formatNumber(totalAmount, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );

  if (totalDeductions != 0) {
    add(
      formatColumnsRight(
        ['Total Deductions:', formatNumber(totalDeductions, 2, 2)],
        separatorNo,
        [1, 1],
      ),
    );
  }
  add(
    formatColumnsRight(
      ['Total Payables:', formatNumber(totalPayables, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );
  payments?.forEach((payment) => {
    if (payment?.total_amount != 0) {
      add(
        formatColumnsRight(
          [`${payment?.name}:`, formatNumber(payment?.total_amount, 2, 2)],
          separatorNo,
          [1, 1],
        ),
      );
    }
  });

  add(
    formatColumnsRight(
      ['Change:', formatNumber(change, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );
  payments?.forEach((payment) => {
    if (payment?.details && payment?.details?.length > 0) {
      add(`${payment?.name}`);
      payment?.details.forEach((detail) => {
        const remarks = detail?.remarks || '';
        add(`   ${remarks}`);
      });
    }
  });
  /* ======================================================
    GROUP SHARE & SIGNATURES
 ====================================================== */
  const hasAnyValue =
    scPax != 0 ||
    spPax != 0 ||
    athPax != 0 ||
    pwdPax != 0 ||
    scShare != 0 ||
    spShare != 0 ||
    athShare != 0 ||
    pwdShare != 0;

  const hasDetails =
    (scDetails && scDetails.length > 0) ||
    (pwdDetails && pwdDetails.length > 0) ||
    (athleteDetails && athleteDetails.length > 0) ||
    (soloParentDetails && soloParentDetails.length > 0);

  if (groupShare && typeof groupShare === 'object' && hasAnyValue) {
    add(separatorEqual(separatorNo));

    let totalPax = 0;

    // -----------------------
    // REGULAR
    // -----------------------
    if (regShare !== 0) {
      add(
        formatColumnsRight(
          ['REG Share:', formatNumber(regShare, 2, 2)],
          separatorNo,
          [1, 1],
        ),
      );
    }
    if (regPax !== 0) {
      add(formatColumnsRight(['Total REG Pax:', regPax], separatorNo, [1, 1]));
    }

    // -----------------------
    // SENIOR CITIZEN
    // -----------------------
    if (scPax !== 0 && scShare !== 0) {
      add(
        formatColumnsRight(
          ['SC Share:', formatNumber(scShare, 2, 2)],
          separatorNo,
          [1, 1],
        ),
      );
      add(formatColumnsRight(['Total SC Pax:', scPax], separatorNo, [1, 1]));
      totalPax += scPax;
    }

    // -----------------------
    // SOLO PARENT
    // -----------------------
    if (spPax !== 0 && spShare !== 0) {
      add(
        formatColumnsRight(
          ['SP Share:', formatNumber(spShare, 2, 2)],
          separatorNo,
          [1, 1],
        ),
      );
      add(formatColumnsRight(['Total SP Pax:', spPax], separatorNo, [1, 1]));
      totalPax += spPax;
    }

    // -----------------------
    // ATHLETE / NAAC
    // -----------------------
    if (athPax !== 0 && athShare !== 0) {
      add(
        formatColumnsRight(
          ['NAAC Share:', formatNumber(athShare, 2, 2)],
          separatorNo,
          [1, 1],
        ),
      );
      add(formatColumnsRight(['Total NAAC Pax:', athPax], separatorNo, [1, 1]));
      totalPax += athPax;
    }

    // -----------------------
    // PWD
    // -----------------------
    if (pwdPax !== 0 && pwdShare !== 0) {
      add(
        formatColumnsRight(
          ['PWD Share:', formatNumber(pwdShare, 2, 2)],
          separatorNo,
          [1, 1],
        ),
      );
      add(formatColumnsRight(['Total PWD Pax:', pwdPax], separatorNo, [1, 1]));
      totalPax += pwdPax;
    }

    // -----------------------
    // CUSTOMER DETAILS
    // -----------------------
    if (hasDetails) {
      add('CUSTOMER DETAILS');

      scDetails?.forEach(({ idNo, name, tin }) => {
        add(formatTwoColumns('SC Name:', name, separatorNo));
        if (tin) add(formatTwoColumns('SC TIN No.:', tin, separatorNo));
        add(formatTwoColumns('SC ID No.:', idNo, separatorNo));
      });

      pwdDetails?.forEach(({ idNo, name, tin }) => {
        add(formatTwoColumns('PWD Name:', name, separatorNo));
        if (tin) add(formatTwoColumns('PWD TIN No.:', tin, separatorNo));
        add(formatTwoColumns('PWD ID No.:', idNo, separatorNo));
      });

      athleteDetails?.forEach(({ idNo, name }) => {
        add(formatTwoColumns('NAAC Name:', name, separatorNo));
        add(formatTwoColumns('NAAC ID No.:', idNo, separatorNo));
      });

      soloParentDetails?.forEach(({ idNo, name }) => {
        add(formatTwoColumns('SP Name:', name, separatorNo));
        add(formatTwoColumns('SP ID:', idNo, separatorNo));
      });
    }

    // -----------------------
    // SIGNATURES
    // -----------------------
    add(centerText('Signature(s)', separatorNo));
    for (let i = 0; i > totalPax; i--) {
      add(centerText(signatureLine(separatorNo), separatorNo));
    }
  }

  add(separatorEqual(separatorNo));

  /* ======================================================
     VAT BREAKDOWN
  ====================================================== */
  add(
    formatColumnsRight(
      ['VATable Sales:', formatNumber(vatable, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );
  add(
    formatColumnsRight(
      ['VAT Amount:', formatNumber(vatAmount, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );
  add(
    formatColumnsRight(
      ['VAT-Exempt Sales:', formatNumber(vatExempt, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );
  add(
    formatColumnsRight(
      ['Zero-Rated Sales:', formatNumber(zeroRated, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );

  if (serviceCharge != 0 || serviceChargeDeduction != 0)
    add(
      formatColumnsRight(
        [
          `Service Charge (${scPercentage}%):`,
          formatNumber(serviceCharge, 2, 2),
        ],
        separatorNo,
        [1, 1],
      ),
    );

  if (localTax != 0 || localTaxDeduction != 0)
    add(
      formatColumnsRight(
        ['Local Tax:', formatNumber(localTax, 2, 2)],
        separatorNo,
        [1, 1],
      ),
    );

  if (serviceChargeDeduction != 0)
    add(
      formatColumnsRight(
        [
          'Service Charge Deductions:',
          formatNumber(serviceChargeDeduction, 2, 2),
        ],
        separatorNo,
        [1, 1],
      ),
    );

  if (localTaxDeduction != 0)
    add(
      formatColumnsRight(
        ['Local Tax Deductions:', formatNumber(localTaxDeduction, 2, 2)],
        separatorNo,
        [1, 1],
      ),
    );

  if (vatDeduction != 0)
    add(
      formatColumnsRight(
        ['VAT Deductions:', formatNumber(vatDeduction, 2, 2)],
        separatorNo,
        [1, 1],
      ),
    );

  if (discNetOfVat != 0)
    add(
      formatColumnsRight(
        ['Disc Net of VAT:', formatNumber(discNetOfVat, 2, 2)],
        separatorNo,
        [1, 1],
      ),
    );

  add(separatorEqual(separatorNo));

  /* ======================================================
     SIGNATURE SECTION
  ====================================================== */
  add(
    `${taxpayerName ? `Name: ${taxpayerName}` : formatUnderline('Name:', separatorNo)}`,
  );
  add(
    `${guestTIN ? `TIN: ${guestTIN}` : formatUnderline('TIN:', separatorNo)}`,
  );
  add(
    `${guestAddress ? `Address: ${guestAddress}` : formatUnderline('Address:', separatorNo)}`,
  );
  add('');

  if (regDiscount > 0) {
    add(centerText(signatureLine(separatorNo), separatorNo));
    add(centerText('Discount Authorized By', separatorNo));
    add('');
    add(centerText(signatureLine(separatorNo), separatorNo));
    add(centerText('Customer Signature', separatorNo));
    add('');
  }
  add(centerText(signatureLine(separatorNo), separatorNo));
  add(centerText('Voided/Cancelled/Refunded by', separatorNo));
  /* ======================================================
     FOOTER
  ====================================================== */
  add(centerText('POS Provider:', separatorNo));
  add(centerText('SERVO I.T. SOLUTIONS OPC', separatorNo));
  add(centerText('Pio Del Pilar, Makati City', separatorNo));
  add(centerText('PTU: FP082021-000-0000000-00000', separatorNo));
  add(centerText(`TN: ${transNo}`, separatorNo));
  add(centerText('THIS DOCUMENT IS NOT', separatorNo));
  add(centerText('VALID FOR CLAIM OF INPUT TAX', separatorNo));

  return txt;
}

export function generateXReadingTxt(
  data: XReadingJsonData,
  header: Header,
  lineLength: number,
) {
  const {
    salesDate,
    shiftId: shiftID,
    shiftName,
    startedBy: startBy,
    startedDnt: startDT,
    endedBy: endBy,
    endedDnt: endDT,
    paymentDetails = [],
    salesBreakDown: salesDetails = {},
    startInvoiceNO,
    endInvoiceNO,
    voidInvoiceCount,
    voidInvoiceAmount,
  } = data;

  const {
    propertyName = '',
    owner = '',
    street = '',
    barangay = '',
    city = '',
    province = '',
    TIN = '',
    machineNo = '',
    MIN = '',
  } = header || {};

  // safely access inner breakdown
  const salesBreak =
    (salesDetails as SalesBreakdownWrapper)?.salesBreakDown ?? {};

  const {
    sales = 0,
    salesNet = 0,
    salesVat = 0,
    salesServiceCharge = 0,
    salesLocalTax = 0,
    salesTotal = 0,
    salesVatableSales = 0,
    salesVatAmount = 0,
    salesVatExempt = 0,
    salesZeroRated = 0,

    salesTotalDiscounts: {
      stdRegular = 0,
      stdSenior = 0,
      stdPWD = 0,
      stdAth = 0,
      stdSP = 0,
    } = {},

    salesVatDeductions: {
      svdRegular = 0,
      svdSenior = 0,
      svdPWD = 0,
      svdAth = 0,
      svdSP = 0,
      svdZeroRated = 0,
      svdOthers = 0,
    } = {},
  } = salesBreak;

  const separatorNo = lineLength;
  let txt = '';
  const add = (line: string = '') => (txt += line + '\n');

  /* =====================================================
     HEADER
  ===================================================== */
  add(centerText(propertyName, separatorNo));
  add(centerText(owner, separatorNo));
  add(centerText(street, separatorNo));
  add(centerText(`${barangay} ${city} ${province}`, separatorNo));
  add(centerText(`${TIN}`, separatorNo));
  add(centerText(`MIN: ${MIN} / S/N: ${machineNo}`, separatorNo));

  add(separatorAsterisk(separatorNo));

  add(centerText('X-READING', separatorNo));
  add(separatorAsterisk(separatorNo));

  add('');
  add(centerText('SALES DATE', separatorNo));
  add(centerText(salesDate, separatorNo));

  add(`Shift ID: ${shiftID}`);
  add(`Shift Name: ${shiftName}`);
  add(`Start By: ${startBy}`);
  add(`Start D/T: ${startDT}`);
  add(`End By: ${endBy}`);
  add(`End D/T: ${endDT}`);

  /* =====================================================
     PAYMENT DETAILS
  ===================================================== */
  add('');
  add(centerText('=== PAYMENT DETAILS ===', separatorNo));

  paymentDetails?.forEach((p: any) => {
    add(
      formatColumns(
        [`${p?.name}:`, formatNumber(p?.total_amount, 2, 2)],
        [1, 2],
        separatorNo,
      ),
    );
  });

  /* =====================================================
     SALES DETAILS
  ===================================================== */
  add('');
  add(centerText('=== SALES DETAILS ===', separatorNo));

  add(formatColumns(['Sales', formatNumber(sales, 2, 2)], [1, 2], separatorNo));

  add('Total Discounts');

  add(
    formatColumnsRight(
      ['SC Disc:', formatNumber(stdSenior, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );
  add(
    formatColumnsRight(
      ['PWD Disc:', formatNumber(stdPWD, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );
  add(
    formatColumnsRight(
      ['NAAC Disc:', formatNumber(stdAth, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );
  add(
    formatColumnsRight(
      ['Solo Parent Disc:', formatNumber(stdSP, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );
  add(
    formatColumnsRight(
      ['Other Disc:', formatNumber(stdRegular, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );
  add(
    formatColumns(['Net:', formatNumber(salesNet, 2, 2)], [1, 2], separatorNo),
  );
  add(
    formatColumns(
      ['VAT(12%):', formatNumber(salesVat, 2, 2)],
      [1, 2],
      separatorNo,
    ),
  );

  add(
    formatTwoColumns(
      'Service Charge(10%):',
      formatNumber(salesServiceCharge, 2, 2),
      separatorNo,
    ),
  );

  add(
    formatColumns(
      ['Local Tax:', formatNumber(salesLocalTax, 2, 2)],
      [1, 2],
      separatorNo,
    ),
  );

  add(
    formatColumns(
      ['Total:', formatNumber(salesTotal, 2, 2)],
      [1, 2],
      separatorNo,
    ),
  );

  add(
    formatColumns(
      ['VATable Sales:', formatNumber(salesVatableSales, 2, 2)],
      [1, 2],
      separatorNo,
    ),
  );
  add(
    formatColumns(
      ['VAT Amount:', formatNumber(salesVatAmount, 2, 2)],
      [1, 2],
      separatorNo,
    ),
  );

  add(
    formatTwoColumns(
      'VAT-Exempt Sales:',
      formatNumber(salesVatExempt, 2, 2),
      separatorNo,
    ),
  );
  add(
    formatTwoColumns(
      'Zero-Rated Sales:',
      formatNumber(salesZeroRated, 2, 2),
      separatorNo,
    ),
  );

  add('VAT deductions due to...');
  add(
    formatColumnsRight(
      ['SC Trans:', formatNumber(svdSenior, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );
  add(
    formatColumnsRight(
      ['PWD Trans:', formatNumber(svdPWD, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );
  add(
    formatColumnsRight(
      ['NAAC Trans:', formatNumber(svdAth, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );
  add(
    formatColumnsRight(
      ['Solo Parent Trans:', formatNumber(svdSP, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );
  add(
    formatColumnsRight(
      ['Other Disc Trans:', formatNumber(svdRegular, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );
  add(
    formatColumnsRight(
      ['Zero-Rated Trans:', formatNumber(svdZeroRated, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );
  add(
    formatColumnsRight(
      ['Other VAT Deductions:', formatNumber(svdOthers, 2, 2)],
      separatorNo,
      [1, 1],
    ),
  );

  add('');

  add(formatTwoColumns('Start Invoice No:', startInvoiceNO, separatorNo));
  add(formatTwoColumns('End Invoice No:', endInvoiceNO, separatorNo));
  add(formatTwoColumns('Void Invoice Count:', voidInvoiceCount, separatorNo));
  add(
    formatTwoColumns(
      'Void Invoice Amount:',
      formatNumber(voidInvoiceAmount, 2, 2),
      separatorNo,
    ),
  );

  add('');

  // /* =====================================================
  //    TENDERS SUMMARY
  // ===================================================== */
  // add("Tenders Summary");
  // add(separatorLine(separatorNo));

  // const group = tendersSummary?.[0] ?? {};

  // const hasNonZero = (obj, keys) => keys?.some((key) => Number(obj?.[key]) > 0);

  // /* -------------------- BILLS -------------------- */
  // const bills = group.bills || {};
  // const billKeys = [
  //   { key: "amountBill1000", count: "countBill1000", label: "1000.00" },
  //   { key: "amountBill500", count: "countBill500", label: "500.00" },
  //   { key: "amountBill200", count: "countBill200", label: "200.00" },
  //   { key: "amountBill100", count: "countBill100", label: "100.00" },
  //   { key: "amountBill50", count: "countBill50", label: "50.00" },
  //   { key: "amountBill20", count: "countBill20", label: "20.00" },
  // ];

  // if (hasNonZero(bills, billKeys.map((b) => b.key))) {
  //   add("Bills:");
  //   billKeys.forEach(({ key, count, label }) => {
  //     const amount = bills[key];
  //     const qty = bills[count];
  //     if (amount > 0 && qty > 0) {
  //       add(formatColumnsRight([`${label} x ${qty}`, formatNumber(amount, 2, 2)], separatorNo, [1, 1]));
  //     }
  //   });
  // }

  // /* -------------------- COINS -------------------- */
  // const coins = group.coins || {};
  // const coinKeys = [
  //   { key: "amountCoins20", count: "countCoins20", label: "20.00" },
  //   { key: "amountCoins10", count: "countCoins10", label: "10.00" },
  //   { key: "amountCoins5", count: "countCoins5", label: "5.00" },
  //   { key: "amountCoins1", count: "countCoins1", label: "1.00" },
  //   { key: "amountCoins25c", count: "countCoins25c", label: "0.25" },
  //   { key: "amountCoins10c", count: "countCoins10c", label: "0.10" },
  //   { key: "amountCoins05c", count: "countCoins05c", label: "0.05" },
  //   { key: "amountCoins01c", count: "countCoins01c", label: "0.01" },
  // ];

  // if (hasNonZero(coins, coinKeys.map((c) => c.key))) {
  //   add("Coins:");
  //   coinKeys.forEach(({ key, count, label }) => {
  //     const amount = coins[key];
  //     const qty = coins[count];
  //     if (amount > 0 && qty > 0) {
  //       add(formatColumnsRight([`${label} x ${qty}`, formatNumber(amount, 2, 2)], separatorNo, [1, 1]));
  //     }
  //   });
  // }

  // /* -------------------- CARDS -------------------- */
  // const cards = group.cards || {};
  // const cardKeys = [
  //   { key: "totalCardMastercard", label: "Master Card" },
  //   { key: "totalCardVisa", label: "Visa" },
  //   { key: "totalCardJcb", label: "JCB" },
  //   { key: "totalCardHsbc", label: "HSBC" },
  //   { key: "totalCardDiscover", label: "Discover" },
  //   { key: "totalCardAmex", label: "American Express" },
  //   { key: "totalCardOthers", label: "Other Cards" },
  // ];

  // if (hasNonZero(cards, cardKeys.map((c) => c.key))) {
  //   add("Cards:");
  //   cardKeys.forEach(({ key, label }) => {
  //     const amount = cards[key];
  //     if (amount > 0) {
  //       add(formatColumnsRight([label, formatNumber(amount, 2, 2)], separatorNo, [1, 1]));
  //     }
  //   });
  // }

  // /* -------------------- EPAYMENTS -------------------- */
  // const epay = group.epayments || {};
  // const epaymentKeys = [
  //   { key: "totalEpaymentPaypal", label: "Paypal" },
  //   { key: "totalEpaymentGcash", label: "Gcash" },
  //   { key: "totalEpaymentMaya", label: "Maya" },
  //   { key: "totalEpaymentApple", label: "Apple" },
  //   { key: "totalEpaymentGoogle", label: "Google" },
  //   { key: "totalEpaymentOthers", label: "Other Epayment" },
  // ];

  // if (hasNonZero(epay, epaymentKeys.map((e) => e.key))) {
  //   add("Epayments:");
  //   epaymentKeys.forEach(({ key, label }) => {
  //     const amount = epay[key];
  //     if (amount > 0) {
  //       add(formatColumnsRight([label, formatNumber(amount, 2, 2)], separatorNo, [1, 1]));
  //     }
  //   });
  // }

  // /* -------------------- OTHERS -------------------- */
  // const others = group.others || {};
  // const othersKeys = [
  //   { key: "totalOthersCheques", label: "Cheques" },
  //   { key: "totalOthersGiftCertificates", label: "Gift Certificate" },
  //   { key: "totalOthersVouchers", label: "Vouchers" },
  //   { key: "totalOthersOthers", label: "Others" },
  // ];

  // if (hasNonZero(others, othersKeys.map((o) => o.key))) {
  //   add("Others:");
  //   othersKeys.forEach(({ key, label }) => {
  //     const amount = others[key];
  //     if (amount > 0) {
  //       add(formatColumnsRight([label, formatNumber(amount, 2, 2)], separatorNo, [1, 1]));
  //     }
  //   });
  // }

  // add("");

  // /* =====================================================
  //    DECLARED TENDERS
  // ===================================================== */
  // const declared = group.declaredTenders;

  // if (declared) {
  //   add("Declared Tenders");
  //   add(separatorLine(separatorNo));

  //   add(formatColumnsRight(["TOTAL BILLS:", formatNumber(declared.totalBillAmount, 2, 2)], separatorNo, [1, 1]));
  //   add(formatColumnsRight(["TOTAL COINS:", formatNumber(declared.totalCoinsAmount, 2, 2)], separatorNo, [1, 1]));
  //   add(formatColumnsRight(["TOTAL CARDS:", formatNumber(declared.totalCardAmount, 2, 2)], separatorNo, [1, 1]));
  //   add(formatColumnsRight(["TOTAL EPAYMENTS:", formatNumber(declared.totalEpaymentAmount, 2, 2)], separatorNo, [1, 1]));
  //   add(formatColumnsRight(["TOTAL CHEQUES:", formatNumber(declared.totalChequesAmount, 2, 2)], separatorNo, [1, 1]));
  //   add(formatColumnsRight(["TOTAL GCs:", formatNumber(declared.totalGcsAmount, 2, 2)], separatorNo, [1, 1]));
  //   add(formatColumnsRight(["TOTAL OTHERS:", formatNumber(declared.totalOthersAmount, 2, 2)], separatorNo, [1, 1]));
  //   add(formatColumnsRight(["TOTAL COLLECTIONS:", formatNumber(declared.totalCollectionsSubmitted, 2, 2)], separatorNo, [1, 1]));
  //   add(formatColumnsRight(["ACTUAL COLLECTIONS:", formatNumber(declared.totalCollectionsPosted, 2, 2)], separatorNo, [1, 1]));
  //   add(formatColumnsRight(["SHORT/OVER:", formatNumber(declared.shortOver, 2, 2)], separatorNo, [1, 1]));
  //   add("");
  // }

  add(separatorAsterisk(separatorNo));
  add(centerText('******* END OF REPORT *******', separatorNo));

  return txt;
}

export function generateZReadingTxt(
  data: ZReadingJsonData,
  header: Header,
  separatorNo: number,
): string {
  // ---------------------------------
  // SAFE DATA EXTRACTION
  const item = Array.isArray(data) ? (data[0] ?? {}) : (data ?? {});

  const {
    endedDNT = '',
    startedDNT = '',
    salesDateEnd = '',
    salesBreakDown,
    oldGrandTotal,
    newGrandTotal,
    startInvoiceNO,
    endInvoiceNO,
    voidInvoiceAmount,
    startVoidInvoiceNo,
    endVoidInvoiceNo,
    zcount: zCounter,
  } = item;

  const {
    propertyName = '',
    owner = '',
    street = '',
    barangay = '',
    city = '',
    province = '',
    TIN = '',
    machineNo = '',
    MIN = '',
  } = header || {};

  // Fallbacks for nested Z summary data
  const zs = salesBreakDown || {};
  const zsDisc = zs.salesTotalDiscounts || {};
  const zsDed = zs.salesVatDeductions || {};

  const sep = separatorLine(separatorNo);
  let txt = '';
  const add = (line: string = '') => (txt += line + '\n');

  /* =====================================================
     HEADER (CENTERED)
  ===================================================== */
  add(centerText(propertyName, separatorNo));
  add(centerText(owner, separatorNo));
  add(centerText(street, separatorNo));
  add(centerText(`${barangay} ${city} ${province}`, separatorNo));
  add(centerText(`${TIN}`, separatorNo));
  add(centerText(`MIN: ${MIN} / S/N: ${machineNo}`, separatorNo));

  add(centerText('Z-READING REPORT', separatorNo));

  /* =====================================================
     GENERAL INFO
  ===================================================== */
  add(formatTwoColumns('Report Date:', getDate(endedDNT), separatorNo));
  add(formatTwoColumns('Report Time:', getTime(endedDNT), separatorNo));
  add('');
  add(formatTwoColumns('Sales Date:', salesDateEnd, separatorNo));
  add(formatTwoColumns('Start Date & Time:', startedDNT, separatorNo));
  add(formatTwoColumns('End Date & Time:', endedDNT, separatorNo));
  add('');
  add(formatTwoColumns('Beg. INV #:', startInvoiceNO, separatorNo));
  add(formatTwoColumns('End. INV #:', endInvoiceNO, separatorNo));
  add(formatTwoColumns('Beg. VOID #', startVoidInvoiceNo, separatorNo));
  add(formatTwoColumns('End. VOID #:', endVoidInvoiceNo, separatorNo));
  add('');
  add(formatTwoColumns('Z Counter No. :', zCounter, separatorNo));
  add(sep);
  add(
    formatTwoColumns(
      'Present Accumulated Sales:',
      formatNumber(newGrandTotal, 2, 2),
      separatorNo,
    ),
  );
  add(
    formatTwoColumns(
      'Previous Accumulated Sales:',
      formatNumber(oldGrandTotal, 2, 2),
      separatorNo,
    ),
  );
  add(
    formatTwoColumns(
      'Sales for the Day:',
      formatNumber(zs?.salesTotal, 2, 2),
      separatorNo,
    ),
  );
  add(sep);
  add(centerText('BREAKDOWN OF SALES', separatorNo));
  add(
    formatTwoColumns(
      'VATable Sales:',
      formatNumber(zs?.salesVatableSales ?? 0, 2, 2),
      separatorNo,
    ),
  );
  add(
    formatTwoColumns(
      'VAT Amount:',
      formatNumber(zs?.salesVatAmount ?? 0, 2, 2),
      separatorNo,
    ),
  );
  add(
    formatTwoColumns(
      'VAT-Exempt Sales:',
      formatNumber(zs?.salesVatExempt ?? 0, 2, 2),
      separatorNo,
    ),
  );
  add(
    formatTwoColumns(
      'Zero-Rated Sales:',
      formatNumber(zs?.salesZeroRated ?? 0, 2, 2),
      separatorNo,
    ),
  );
  if (zs?.salesServiceCharge != 0) {
    add(
      formatTwoColumns(
        'Service Charge(10%):',
        formatNumber(zs?.salesServiceCharge ?? 0, 2, 2),
        separatorNo,
      ),
    );
  }
  if (zs?.salesLocalTax != 0) {
    add(
      formatTwoColumns(
        'Local Tax:',
        formatNumber(zs?.salesLocalTax ?? 0, 2, 2),
        separatorNo,
      ),
    );
  }
  add(sep);
  add(
    formatTwoColumns(
      'Gross Amount:',
      formatNumber(zs?.salesTotal, 2, 2),
      separatorNo,
    ),
  );
  add(
    formatTwoColumns(
      'Less Discounts:',
      formatNumber(zs?.salesTotalDiscount, 2, 2),
      separatorNo,
    ),
  );
  add(
    formatTwoColumns(
      'Less Void:',
      formatNumber(voidInvoiceAmount, 2, 2),
      separatorNo,
    ),
  );
  add(
    formatTwoColumns(
      'Less VAT Deductions:',
      formatNumber(zs?.salesTotalVatDeduction ?? 0, 2, 2),
      separatorNo,
    ),
  );
  add(
    formatTwoColumns(
      'Less Service Charge Deductions:',
      formatNumber(zs?.salesTotalServiceChargeDeduction ?? 0, 2, 2),
      separatorNo,
    ),
  );
  add(
    formatTwoColumns(
      'Net Amount:',
      formatNumber(zs?.salesNet, 2, 2),
      separatorNo,
    ),
  );
  add(sep);
  add(centerText('DISCOUNT SUMMARY', separatorNo));
  [
    ['SC Disc:', zsDisc?.stdSenior],
    ['PWD Disc:', zsDisc?.stdPWD],
    ['NAAC Disc:', zsDisc?.stdAth],
    ['Solo Parent Disc:', zsDisc?.stdSP],
    ['Other Disc:', zsDisc?.stdRegular],
  ].forEach(([label, value]) =>
    add(formatTwoColumns(label, formatNumber(value ?? 0, 2, 2), separatorNo)),
  );
  add(sep);
  add(centerText('SALES ADJUSTMENT', separatorNo));
  add(
    formatTwoColumns(
      'VOID : ',
      formatNumber(voidInvoiceAmount, 2, 2),
      separatorNo,
    ),
  );
  add(sep);
  add(centerText('VAT DEDUCTIONS', separatorNo));
  [
    ['SC Trans :', zsDed?.svdSenior],
    ['PWD Trans :', zsDed?.svdPWD],
    ['NAAC Trans:', zsDed?.svdAth],
    ['Solo Parent Trans:', zsDed?.svdSP],
    ['Other Trans :', zsDed?.svdRegular],
    ['Zero-Rated Trans :', zsDed?.svdZeroRated],
    ['Other VAT Deductions :', zsDed?.svdOthers],
  ].forEach(([label, value]) =>
    add(formatTwoColumns(label, formatNumber(value ?? 0, 2, 2), separatorNo)),
  );
  add(sep);
  add(centerText('******* END OF REPORT *******', separatorNo));

  return txt;
}

export function downloadEJournalTxt(text: string) {
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = 'E-JOURNAL.txt';
  a.click();

  URL.revokeObjectURL(url);
}
